/** Utility functions for task mutations, separated from the React hook logic. */
// @ts-check
import { isFuture, isValid, parse, subMinutes, format } from 'date-fns';
import { assertSchedulableScheduleUpdate } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import { applyDetailUpdates } from '@/features/tasks/utils/mutations/taskMutationPropagation';

/**
 * Determines whether a task's start date/time should be moved to "now" when
 * the task is marked as completed.
 *
 * @param {Task | null | undefined} task
 * @returns {boolean}
 */
export const shouldAdjustDateTime = (task) => {
  if (!task) return false;
  if (!task.date || !task.time) {
    return true;
  }

  try {
    const dateTimeString = `${task.date} ${task.time}`;
    const parsedDateTime = parse(
      dateTimeString,
      'yyyy-MM-dd HH:mm',
      new Date(),
    );
    if (!isValid(parsedDateTime)) return false;
    return isFuture(parsedDateTime);
  } catch (error) {
    console.error('Error parsing task date/time for adjustment check:', error);
    return false;
  }
};

/**
 * @param {UpdateTask} details
 * @param {Task | undefined} originalTask
 */
export const adjustDateTimeIfCompleting = (details, originalTask) => {
  if (!details.completed || !originalTask || originalTask.completed) return;
  if (shouldAdjustDateTime(originalTask)) {
    const newStartDateTime = subMinutes(new Date(), originalTask.duration || 0);
    details.date = format(newStartDateTime, 'yyyy-MM-dd');
    details.time = format(newStartDateTime, 'HH:mm');
  }
};

/**
 * @param {UpdateTask} validTaskData
 * @returns {Partial<Task>}
 */
export const getStructuralChanges = (validTaskData) => {
  const structuralChanges = {};
  if (validTaskData.parentId !== undefined)
    structuralChanges.parentId = validTaskData.parentId;
  if (validTaskData.orderKey !== undefined)
    structuralChanges.orderKey = validTaskData.orderKey;
  return structuralChanges;
};

/**
 * @param {Partial<Task>} structuralChanges
 * @param {UpdateTask} details
 * @param {Task[]} allTasks
 */
export const inheritParentProjects = (structuralChanges, details, allTasks) => {
  if (
    structuralChanges.parentId === undefined ||
    structuralChanges.parentId === null
  )
    return;
  const newParent = allTasks.find((t) => t.id === structuralChanges.parentId);
  if (newParent?.projects?.length > 0) {
    details.projects = [...newParent.projects];
  }
};

/**
 * Calculates the new reschedule count for a task based on its current state and the proposed updates.
 *
 * Rules:
 * 1. If moved to Backlog, count is reset to 0.
 * 2. If moving from Backlog to Active with a date, count starts at 1.
 * 3. If Active and date changes (including null to date, date to date, or date to null), count increments.
 * 4. Otherwise, count remains unchanged.
 *
 * @param {Task | undefined} originalTask
 * @param {UpdateTask} taskData
 * @returns {number}
 */
export const calculateNewRescheduleCount = (originalTask, taskData) => {
  if (!originalTask) return 0;

  const oldStatus = originalTask.status;
  const newStatus = taskData.status || oldStatus;

  if (newStatus === 'Backlog') return 0;

  const oldDate = originalTask.date || null;
  const newDate = taskData.date === undefined ? oldDate : taskData.date || null;

  if (oldDate === newDate) return originalTask.rescheduleCount || 0;

  if (oldStatus === 'Backlog' && newStatus === 'Active') {
    return newDate ? 1 : 0;
  }

  if (!oldDate && newDate) return 1;
  return (originalTask.rescheduleCount || 0) + 1;
};

/**
 * @param {{
 *   taskId: string,
 *   taskData: UpdateTask,
 *   userId: string,
 *   allTasks: Task[],
 *   moveTask: any,
 *   taskTree: any[],
 *   getAllDescendantIds: (taskId: string, taskTree: any[]) => string[],
 *   updateTaskDetails: (taskId: string, details: UpdateTask, userId: string) => Promise<void>
 * }} params
 */
export const executeTaskUpdate = async (params) => {
  const {
    taskId,
    taskData,
    userId,
    allTasks,
    moveTask,
    taskTree,
    getAllDescendantIds,
    updateTaskDetails,
  } = params;

  try {
    const originalTask = allTasks.find((t) => t.id === taskId);
    const { parentId, orderKey, ...details } = { ...taskData };

    adjustDateTimeIfCompleting(details, originalTask);

    if (originalTask) {
      const newRescheduleCount = calculateNewRescheduleCount(
        originalTask,
        details,
      );
      if (newRescheduleCount !== (originalTask.rescheduleCount || 0)) {
        details.rescheduleCount = newRescheduleCount;
      }
    }

    const structuralChanges = getStructuralChanges({ parentId, orderKey });
    inheritParentProjects(structuralChanges, details, allTasks);

    if (Object.keys(details).length > 0) {
      assertSchedulableScheduleUpdate(originalTask, details, allTasks);
    }

    if (Object.keys(structuralChanges).length > 0 && moveTask) {
      await moveTask(taskId, structuralChanges, userId, originalTask);
    }

    if (Object.keys(details).length > 0) {
      await applyDetailUpdates(
        taskId,
        details,
        originalTask,
        allTasks,
        taskTree,
        getAllDescendantIds,
        updateTaskDetails,
        userId,
      );
    }
  } catch (error) {
    console.error('[executeTaskUpdate] Error:', error);
    throw error;
  }
};

export { getDescendantIds } from '@/features/tasks/utils/mutations/taskMutationPropagation';
